import React from 'react';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { Tooltip } from '@mui/material';
import { NavLink } from 'react-router-dom';
import {
  isPossiblePhoneNumber,
  formatPhoneNumberIntl,
} from 'react-phone-number-input';
import { IFormFieldInputBaseProps } from '@components/form-fields';
import {
  addZeroesAndSeparatevalue,
  maskValueWithPatternIfMatch,
  normaliseUrl,
  trimTrailingZeros,
  valueCommaSeparated,
} from '@utils/formatting-utils';
import { MAIN_THEME } from '@styles';
import { isZeroValue } from '@utils/validationUtils';
import DateService from '@services/DateService';
import PrecisionService from '@services/PrecisionService';
import { TextMode } from '.';

export interface IEnhancedDisplayProps extends IFormFieldInputBaseProps {
  title: string;
  iconUrl?: string;
  value?: string | JSX.Element;
  textFormatting?: EnhancedDisplayTextFormatting[];
  type?: EnhancedDisplayType;
  multiline: boolean;
  url?: string;
  displayInline?: boolean;
  toolTip?: boolean;
  currencySymbol?: string;
  phoneNumberPattern?: string;
  classes?: { propertyTitle?: string };
  copyClipboardButton?: boolean;
  openUrlInNewTab?: boolean;
  textMode?: TextMode;
  tooltipContents?: JSX.Element;
  fullLine?: boolean;
}

export enum EnhancedDisplayTextFormatting {
  Bold = 'Bold',
  Italic = 'Italic',
  Underlined = 'Underlined',
}

export enum EnhancedDisplayType {
  DateTime = 'DateTime',
  Date = 'Date',
  Email = 'Email',
  RelativeLink = 'RelativeLink',
  ExternalLink = 'ExternalLink',
  PhoneNumber = 'PhoneNumber',
  IntPhoneNumber = 'IntPhoneNumber',
  Currency = 'Currency',
  RoundedCurrency = 'RoundedCurrency',
  Percentage = 'Percentage',
  RoundedPercentage = 'RoundedPercentage',
}

// type IDisplayStyleProps = Pick<IEnhancedDisplayProps, 'displayInline'> & {
//   singleLine: boolean;
// };

const useStyles = makeStyles<{ displayInline: boolean; singleLine: boolean }>()(
  (theme, { displayInline, singleLine }) => ({
    property: {
      display: singleLine ? 'flex' : undefined,
      alignContent: singleLine ? 'center' : undefined,
      width: '100%',
      textAlign: 'left',
      overflow: 'hidden',
      margin: displayInline ? '0 0 7px' : '0 0 18px',
      minWidth: displayInline ? 100 : 'unset',
      lineHeight: displayInline ? '20px' : 'unset',
    },
    propertyTitle: {
      color: displayInline ? MAIN_THEME.color.black : MAIN_THEME.color.black,
      fontSize: MAIN_THEME.fontSize.rg,
      lineHeight: displayInline ? '20px' : '12px',
      flex: singleLine ? '1 0 0' : undefined,
      fontWeight: 'bold',
      fontFamily: MAIN_THEME.font.medium,
    },
    propertyValue: {
      display: displayInline ? 'inline' : 'block',
      margin: displayInline ? '0' : '4px 4px 2px 0',
      color: displayInline ? MAIN_THEME.color.black : MAIN_THEME.color.black,
      fontSize: MAIN_THEME.fontSize.rg,
      lineHeight: displayInline ? '15px' : '17px',
      fontFamily: MAIN_THEME.font.regular,
      flex: singleLine ? '1 1 0' : undefined,
      textAlign: singleLine ? 'center' : undefined,
    },
    buttonLink: {
      border: 0,
      backgroundColor: 'transparent',
      textDecoration: 'underline',
      padding: '0',
      cursor: 'pointer',
    },
    icon: {
      width: '15px',
      height: '15px',
      display: 'inline-block',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'contain',
      backgroundColor: 'transparent',
      backgroundPosition: 'center',
      verticalAlign: 'bottom',
      margin: '0 5px 0 0',
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.5) !important',
      color: `${MAIN_THEME.color.white} !important`,
      fontSize: MAIN_THEME.fontSize.sm,
      border: 'none',
      boxShadow: 'none',
    },
    tooltipTitle: {
      color: `${MAIN_THEME.color.black}!important`,
    },
    tooltipSpan: {
      display: 'inline-block',
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      backgroundColor: 'red',
      color: MAIN_THEME.color.white,
      textAlign: 'center',
      fontSize: MAIN_THEME.fontSize.xs,
      lineHeight: '12px',
      marginRight: '6px',
      cursor: 'cursor',
    },
  })
);

const EnhancedDisplay: React.FC<IEnhancedDisplayProps> = ({
  className,
  title,
  iconUrl,
  value,
  url,
  type,
  displayInline,
  textFormatting,
  toolTip,
  classes = {},
  currencySymbol,
  openUrlInNewTab = false,
  multiline,
  textMode,
  tooltipContents,
}) => {
  const singleLine = multiline && textMode === TextMode.SingleLine;
  const { classes: displayClasses } = useStyles({ displayInline, singleLine });

  function formatValue(input: string | JSX.Element | undefined) {
    let outputEl: JSX.Element = (
      <p className={displayClasses.propertyValue}>
        {isZeroValue(input) ? 0 : input || '- -'}
      </p>
    );
    if (input) {
      switch (type) {
        case EnhancedDisplayType.Email:
          outputEl = (
            <a
              className={displayClasses.propertyValue}
              href={`mailto: ${input}`}
            >
              {input}
            </a>
          );
          break;
        case EnhancedDisplayType.PhoneNumber:
          <p className={displayClasses.propertyValue}>{input}</p>;
          break;
        case EnhancedDisplayType.RelativeLink:
          outputEl = (
            <NavLink
              className={displayClasses.propertyValue}
              to={url || '/'}
              target={openUrlInNewTab ? '_blank' : undefined}
            >
              {input}
            </NavLink>
          );
          break;
        case EnhancedDisplayType.ExternalLink:
          outputEl = (
            <a
              className={displayClasses.propertyValue}
              target="_blank"
              rel="noopener noreferrer"
              href={normaliseUrl(url || '')}
            >
              {input}
            </a>
          );
          break;
        default:
          break;
      }
    }

    if (textFormatting) {
      textFormatting.forEach((format) => {
        switch (format) {
          case EnhancedDisplayTextFormatting.Bold:
            outputEl = <b>{outputEl}</b>;
            break;
          case EnhancedDisplayTextFormatting.Italic:
            outputEl = <i>{outputEl}</i>;
            break;
          case EnhancedDisplayTextFormatting.Underlined:
            outputEl = <u>{outputEl}</u>;
            break;
          default:
            break;
        }
      });
    }

    return outputEl;
  }

  const getValue = () => {
    let output: string | JSX.Element | undefined = value;

    if (typeof output === 'string') {
      if (output) {
        switch (type) {
          case EnhancedDisplayType.Date:
            output = DateService.formatDate(output);
            break;
          case EnhancedDisplayType.DateTime:
            output = DateService.formatDateTime(output);
            break;
          case EnhancedDisplayType.Currency:
            output = output
              ? `${currencySymbol} ${addZeroesAndSeparatevalue(output)}`
              : output;
            break;
          case EnhancedDisplayType.RoundedCurrency:
            output = output
              ? `${currencySymbol} ${trimTrailingZeros(valueCommaSeparated(output), 3, true)}`
              : output;
            break;
          case EnhancedDisplayType.Percentage:
            output = Number(output)
              ? `${valueCommaSeparated(PrecisionService.multiplyBy100(Number(output)))}%`
              : `${output} %`;
            break;
          case EnhancedDisplayType.RoundedPercentage:
            output = Number(output)
              ? `${trimTrailingZeros(valueCommaSeparated(PrecisionService.multiplyBy100(Number(output))), 3, true)}%`
              : `${output} %`;
            break;
          case EnhancedDisplayType.PhoneNumber: {
            output =
              output && '+1 (xxx) xxx-xxxx'
                ? maskValueWithPatternIfMatch(output, '+1 (xxx) xxx-xxxx')
                : output;
            break;
          }
          case EnhancedDisplayType.IntPhoneNumber: {
            if (output && isPossiblePhoneNumber(output)) {
              output = formatPhoneNumberIntl(output as any);
            }
            break;
          }
          default:
            break;
        }
      }
    }
    return output;
  };

  const renderTitle = () => {
    return (
      <>
        {iconUrl && (
          <figure
            className={displayClasses.icon}
            style={{ backgroundImage: `url(${iconUrl})` }}
          />
        )}

        {title && (displayInline ? `${title}: ` : title)}
      </>
    );
  };

  const displayValue = getValue();

  return (
    <div
      className={clsx(displayClasses.property, className)}
      style={multiline ? { width: '100%' } : undefined}
    >
      {toolTip ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              placement="bottom-start"
              title={tooltipContents}
              classes={{ tooltip: displayClasses.tooltip }}
            >
              <span className={displayClasses.tooltipSpan}>!</span>
            </Tooltip>
            <span
              className={clsx(
                displayClasses.propertyTitle,
                classes.propertyTitle
              )}
            >
              {renderTitle()}
            </span>
          </div>
          <div style={{ marginTop: '4px' }}>{formatValue(displayValue)}</div>
        </>
      ) : (
        <>
          <span
            className={clsx(
              displayClasses.propertyTitle,
              classes.propertyTitle
            )}
          >
            {renderTitle()}
          </span>
          {formatValue(displayValue)}
        </>
      )}
    </div>
  );
};

export default EnhancedDisplay;
